import { AUTH_TOKEN, CURRENT_USER, REFRESH_TOKEN } from '../constants/projectConstants';
import { USERPILOT_UUID } from '../constants/constants';
import { AI_WEBSITE_URL } from '../constants/routePathConstants';
import { getLocalStorage } from './jsHelper';

/** The param AI Studio reads the handed-over Userpilot id from. */
export const USERPILOT_ID_PARAM = 'user_pilot_id';

/**
 * The BUTTON a link into AI Studio was clicked on. Sent as `click_from` (AI Studio's
 * `trackUTMVisitEvent` already reads it) and, slugged, as the `utm_campaign` default — so a report
 * can say "120 arrived from Magic Tool, 40 from the nav menu" instead of just "160 arrived".
 *
 * Human labels, because `click_from` has always carried one ("App Dashboard") and a dashboard
 * filtering on that value shouldn't stop matching. `campaignOf` derives the wire slug.
 */
export const AI_STUDIO_SOURCE = {
  NAV_MENU: 'Nav Menu',
  WORKFLOW_BUILDER: 'Workflow Builder',
  APP_DASHBOARD: 'App Dashboard',
  EDIT_WITH_AI: 'Edit with AI',
  MAGIC_TOOL: 'Magic Tool',
  RE_USE: 'Re-use',
  PRICING: 'Pricing',
  AI_VIDEO_POPUP: 'AI Video Popup',
  CREATIONS: 'Creations Link',
};

/** "Nav Menu" -> "nav_menu", "Re-use" -> "re_use". */
const campaignOf = (source = '') =>
  source
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

/**
 * The id this app identifies to Userpilot with, for handing over to AI Studio. Both apps report to
 * the same Userpilot account, and AI Studio can't always ask the backend for one, so whoever leaves
 * here should arrive there as the same person rather than as a second, empty record.
 *
 * Read off the PERSISTED user, not `getCurrentUser()`: that returns `userData.currentUser`, a
 * module-level mirror which `setCurrentUser` fills only when the object it is handed carries an
 * `organization`, and which then holds just that one call's object rather than the merged user — so
 * it is null on most paths and incomplete on the rest. The localStorage copy is written on EVERY
 * `setCurrentUser` and is the merged `{ ...currentUser, ...user }`, which is where `user_pilot_id`
 * actually lands (`UserContext`, and `info` on the login path).
 *
 * `USERPILOT_UUID` last: it only holds an id for a browser that registered here or arrived from the
 * website with `?uuid=`, so it is empty for an ordinary sign-in.
 */
export const getUserpilotId = () =>
  getLocalStorage(CURRENT_USER, true)?.user_pilot_id || getLocalStorage(USERPILOT_UUID);

/**
 * A UTM value for the link we're about to build: the one that brought the user INTO this app if
 * there was one (remembered for the session, so it survives navigation), else our own default.
 *
 * A real campaign outranks the button name on purpose — someone who arrived on a campaign link and
 * then clicked through to AI Studio is still that campaign's visit.
 */
const getUtmParam = (searchParams, key, defaultValue = '') => {
  const urlValue = searchParams.get(key);
  if (urlValue) {
    sessionStorage.setItem(key, urlValue);
    return urlValue;
  }
  return sessionStorage.getItem(key) || defaultValue;
};

/**
 * THE way to build a link into AI Studio. Every entry point goes through here — the nav menu, the
 * dashboard cards, Magic Tool, Re-use, Edit with AI, the pricing link, the video popup and the
 * workflow builder — so a link can't be missing a piece: it carries the auth tokens AI Studio needs
 * to keep the user signed in, our Userpilot id, and where it was clicked.
 *
 * That mattered: when each caller assembled its own URL, one sent no `refresh_token` (so that user
 * was signed out the moment their access token expired), six sent no Userpilot id, and seven said
 * nothing about which button was used.
 *
 * @param {string} path      AI Studio path, e.g. `discover/2`, `magic-tool`, `pricing/`.
 * @param {object} params    Extra query params; falsy values are dropped.
 * @param {object} options   `source` — one of `AI_STUDIO_SOURCE`. `base` — a different root, for
 *                           the workflow builder, which has its own env URL.
 */
export const buildAIStudioUrl = (path = '', params = {}, options = {}) => {
  const { source = '', base: baseOverride } = options;

  const token = getLocalStorage(AUTH_TOKEN);
  const refreshToken = getLocalStorage(REFRESH_TOKEN);
  const userPilotId = getUserpilotId();

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) searchParams.set(key, value);
  });

  if (source && !searchParams.has('click_from')) searchParams.set('click_from', source);

  const currentParams = new URLSearchParams(window.location.search);
  const utm = {
    utm_source: getUtmParam(currentParams, 'utm_source', 'imagine_platform'),
    utm_medium: getUtmParam(currentParams, 'utm_medium', 'internal'),
    utm_campaign: getUtmParam(currentParams, 'utm_campaign', campaignOf(source)),
    utm_content: getUtmParam(currentParams, 'utm_content', ''),
    utm_term: getUtmParam(currentParams, 'utm_term', ''),
  };
  Object.entries(utm).forEach(([key, value]) => {
    if (value) searchParams.set(key, value);
  });

  if (userPilotId) searchParams.set(USERPILOT_ID_PARAM, userPilotId);
  if (token) searchParams.set('token', token);
  if (refreshToken) searchParams.set('refresh_token', refreshToken);

  /*
    The trailing slash is only added when there IS a path to join on, so a root URL keeps the exact
    shape it had — `WORKFLOW_BUILDER_URL` names a route of its own and gains no `/` before its query.
  */
  const root = baseOverride || AI_WEBSITE_URL || '';
  const cleanPath = path.replace(/^\//, '');
  const base = cleanPath ? `${root.endsWith('/') ? root : `${root}/`}${cleanPath}` : root;

  const query = searchParams.toString();
  return `${base}${query ? `?${query}` : ''}`;
};
